import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Collapse,
	IconButton,
	Stack,
	SvgIcon,
	Typography,
} from '@mui/material';
import dayjs from 'dayjs';

import { TripTypeEnum, VehicleType } from '@mopla/data-models';
import { renderMainTransportIcon } from '@mopla/ui';
import { ChevronIcon } from '@mopla/ui/icons/Chevron';
import ClockIcon from '@mopla/ui/icons/ClockIcon.svg';
import LocationStopV2Icon from '@mopla/ui/icons/LocationStopV2.svg';
import { formatTime, getDurationStr } from '@mopla/utils';

import { IconWrapper } from '../styled';

import {
	DetailTextStyled,
	TransportCard,
	TransportCardInfo,
	TransportCardInfoContainer,
	TransportCardInfoSubtitle,
} from './styled';
import { IIntermediateStop, TTransportMode } from './types';

interface IBookingRouteTransportCardProps {
	title: string;
	duration: number;
	mode?: TTransportMode;
	vehicleType?: VehicleType;
	lineNumber?: string;
	price?: string;
	intermediateStops?: IIntermediateStop[];
}

/**
 * Transport segment card for route display.
 *
 * Two variants:
 * 1. With intermediateStops: Shows stops count + duration, expandable chevron
 * 2. Without intermediateStops: Shows duration + price on right
 */
export const BookingRouteTransportCard: FC<IBookingRouteTransportCardProps> = ({
	title,
	duration,
	mode = TripTypeEnum.LBT,
	vehicleType,
	lineNumber,
	price,
	intermediateStops,
}) => {
	const { t } = useTranslation('common');

	const [expanded, setExpanded] = useState(false);
	const isPublicTransport =
		mode === TripTypeEnum.LBT || mode === TripTypeEnum.LBODT;

	const durationStr = useMemo(() => {
		const durationObj = dayjs.duration(duration, 'seconds');
		return getDurationStr(t, durationObj, {
			withAround: true,
			exclude: ['seconds'],
		});
	}, [t, duration]);

	const hasIntermediateStops =
		intermediateStops && intermediateStops.length > 0;

	return (
		<TransportCard>
			<TransportCardInfoContainer>
				<TransportCardInfo>
					<IconWrapper size={32}>
						{renderMainTransportIcon({ mode, vehicleType }, 's')}
					</IconWrapper>
					<Stack>
						<Typography variant="b2-m">
							{lineNumber ? `${lineNumber} ${title}` : title}
						</Typography>
						<TransportCardInfoSubtitle>
							{isPublicTransport && (
								<DetailTextStyled>
									<SvgIcon
										component={LocationStopV2Icon}
										sx={{ fontSize: '12px' }}
									/>
									<Typography variant="l1">
										{t('text.stops', {
											count: intermediateStops?.length
												? intermediateStops.length + 1
												: 1,
										})}
									</Typography>
								</DetailTextStyled>
							)}
							<DetailTextStyled>
								<SvgIcon component={ClockIcon} sx={{ fontSize: '12px' }} />
								<Typography variant="l1">{durationStr}</Typography>
							</DetailTextStyled>
						</TransportCardInfoSubtitle>
					</Stack>
				</TransportCardInfo>

				{/* Right content: Price or Expand toggle */}
				{isPublicTransport
					? hasIntermediateStops && (
							<IconButton
								onClick={() => setExpanded((view) => !view)}
								data-testid="bookingRoute-expandStops-button"
							>
								<ChevronIcon
									direction={expanded ? 'up' : 'down'}
									fontSize="sm"
								/>
							</IconButton>
					  )
					: price && <Typography variant="b2">{price}</Typography>}
			</TransportCardInfoContainer>

			{/* Expandable intermediate stops */}
			{hasIntermediateStops && (
				<Collapse in={expanded}>
					<Stack gap={1.5}>
						{intermediateStops?.map((stop, i) => (
							<DetailTextStyled key={i}>
								<Typography variant="l1">{formatTime(stop.time)}</Typography>
								<Typography variant="l1">{stop.name}</Typography>
							</DetailTextStyled>
						))}
					</Stack>
				</Collapse>
			)}
		</TransportCard>
	);
};
